import React from "react";
import { Box, Divider, Stack, Text } from "@chakra-ui/react";
import {
  HiOutlineLocationMarker,
  HiOutlineMail,
  HiOutlineOfficeBuilding,
  HiOutlinePhone,
  HiOutlineUserCircle,
} from "react-icons/hi";
import { OrganizationOneTimeRequestAddressSummary } from "@/components/orders/types";
import {
  ClientProfileTypeMarker,
  InfoLine,
  SectionEyebrow,
} from "../components/ClientProfileType";
import { ClientProfileRenderer } from "../registry.types";

const ORGANIZATION_ACCENT = "#6B4BD6";

const formatAddress = (address?: OrganizationOneTimeRequestAddressSummary | null) => {
  if (!address) {
    return null;
  }
  if (address.formattedAddress) {
    return address.formattedAddress;
  }
  return [address.address, address.city, address.state].filter(Boolean).join(", ");
};

export const organizationRenderer: ClientProfileRenderer<"ORGANIZATION"> = {
  badge: { label: "Organization", colorScheme: "purple" },
  rowDisplayName: order => order.clientProfile.organization?.name || "Unnamed organization",
  RowSummary: ({ order }) => (
    <Stack spacing="2px" lineHeight="short">
      <Text fontWeight="medium" color="gray.800">
        {order.clientProfile.organization?.name || "Unnamed organization"}
      </Text>
      <ClientProfileTypeMarker
        accent={ORGANIZATION_ACCENT}
        icon={HiOutlineOfficeBuilding}
        label="Organization"
      />
    </Stack>
  ),
  DetailClientCard: ({ order }) => {
    const orgName = order.clientProfile.organization?.name || "Unnamed organization";
    return (
      <Box
        borderLeft="3px solid"
        borderLeftColor={ORGANIZATION_ACCENT}
        pl="14px"
      >
        <SectionEyebrow
          accent={ORGANIZATION_ACCENT}
          icon={HiOutlineOfficeBuilding}
          label="Organization"
        />
        <Text
          fontSize="lg"
          fontWeight="semibold"
          color="gray.900"
          lineHeight="short"
        >
          {orgName}
        </Text>
      </Box>
    );
  },
  DetailAddressBillingCard: ({ order }) => {
    const request = order.organizationOneTimeRequest;
    if (!request) {
      return null;
    }
    const facilityName = request.facility?.name || request.facilityNameSnapshot || "Unnamed facility";
    const facilityAddress = formatAddress(request.facilityAddressSnapshot);
    const contactName = request.facilityContact?.name || request.facilityContactNameSnapshot;
    const contactPhone = request.facilityContact?.phoneNumber || request.facilityContactPhoneSnapshot;
    const contactEmail = request.facilityContact?.email || request.facilityContactEmailSnapshot;
    const hasContact = Boolean(contactName || contactPhone || contactEmail);

    return (
      <Box>
        <SectionEyebrow icon={HiOutlineLocationMarker} label="Facility" />
        <Text fontSize="md" fontWeight="semibold" color="gray.900" mb="6px">
          {facilityName}
        </Text>
        {facilityAddress && (
          <Text fontSize="sm" color="gray.600" mb={hasContact ? "14px" : 0}>
            {facilityAddress}
          </Text>
        )}
        {hasContact && (
          <>
            <Divider mb="14px" />
            <SectionEyebrow icon={HiOutlineUserCircle} label="Contact" />
            <Stack spacing="6px">
              {contactName && (
                <Text fontSize="sm" fontWeight="semibold" color="gray.900">
                  {contactName}
                </Text>
              )}
              {contactPhone && <InfoLine icon={HiOutlinePhone}>{contactPhone}</InfoLine>}
              {contactEmail && <InfoLine icon={HiOutlineMail}>{contactEmail}</InfoLine>}
            </Stack>
          </>
        )}
      </Box>
    );
  },
};
